import React from 'react';
import {
  authenticateWithPasskey,
  login,
  loginWithGoogle,
} from '../../../services/auth';
import { Input, PasswordInput } from '../../../components/Form';
import { Box, Link, Text } from 'rebass';
import Button from '../../../components/Button/Button';
import { useParams } from 'react-router-dom';
import AuthForm from '../components/AuthForm';
import ExternalAuthButtons from './ExternalAuthButtons';
import { takeLoginPath } from './loginRedirect';
import { useOnMountUnsafe } from '@/helpers/useOnMountUnsafe';
import {
  browserSupportsWebAuthnAutofill,
  WebAuthnError,
} from '@simplewebauthn/browser';
import theme from '@/theme';
import Spinner from '@/components/Spinner/Spinner';

/**
 * @component for login form
 */
const LoginForm: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [pw, setPW] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const { authIssuer } = useParams<{ authIssuer?: string }>();
  const [isLoading, setIsLoading] = React.useState(!!authIssuer);

  const handleGoogleLogin = async (code: string) => {
    try {
      await loginWithGoogle({ code });
    } catch (err) {
      setErrorMessage((err as Error).message);
      setIsLoading(false);
    }
  };

  useOnMountUnsafe(() => {
    if (authIssuer === 'google') {
      const searchParams = new URLSearchParams(window.location.search);
      const authCode = searchParams.get('code');
      const returnedState = searchParams.get('state');
      const expectedState = sessionStorage.getItem('google-oauth-state');

      sessionStorage.removeItem('google-oauth-state');
      // Restore ?redirect= so goToHomePage sends the user back where they started.
      window.history.replaceState(null, '', takeLoginPath(sessionStorage));

      console.log('authCode', authCode);
      console.log('returnedState', returnedState);
      console.log('expectedState', expectedState);

      if (
        !authCode ||
        !returnedState ||
        !expectedState ||
        returnedState !== expectedState
      ) {
        setErrorMessage('Sign-in failed: invalid or missing state parameter.');
        setIsLoading(false);
        return;
      }

      handleGoogleLogin(authCode);
    }
  });

  // Handles when form is submitted
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await login(email, pw);
    } catch (err) {
      setErrorMessage((err as Error).message);
    }
  };

  // When input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');
    e.target.name === 'email'
      ? setEmail(e.target.value)
      : setPW(e.target.value);
  };

  useOnMountUnsafe(() => {
    browserSupportsWebAuthnAutofill().then(async (supports) => {
      if (supports) {
        try {
          await authenticateWithPasskey(true);
        } catch (err) {
          if (
            err instanceof WebAuthnError &&
            err.code === 'ERROR_CEREMONY_ABORTED'
          ) {
            return;
          } else {
            console.error(err);
          }
        }
      }
    });
  });

  return (
    <AuthForm onSubmit={handleSubmit}>
      <Input
        id="input-email"
        type="email"
        aria-label={`Email`}
        placeholder="Email"
        mt={5}
        padding={5}
        name="email"
        value={email}
        onChange={handleInputChange}
        disabled={isLoading}
        autoComplete="username webauthn"
      />
      <PasswordInput
        id="input-password"
        name="password"
        aria-label={`Password`}
        placeholder="Password"
        mt={3}
        padding={5}
        value={pw}
        onChange={handleInputChange}
        showPassword={showPassword}
        toggleShowPassword={() => setShowPassword(!showPassword)}
        disabled={isLoading}
      />

      <Box textAlign="right" mt={1}>
        <Link
          fontSize={0}
          color={theme.colors.secondary}
          pr={2}
          href={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
        >
          Forgot Password?
        </Link>
      </Box>

      {errorMessage && !isLoading && (
        <Text variant="subheading" mt={2} sx={{ color: '#D9534F' }}>
          {errorMessage}
        </Text>
      )}
      <Button
        width="100%"
        mt={errorMessage ? 2 : 6}
        justifyContent="center"
        paddingY={3}
        type="submit"
        variant="primary"
      >
        Login
      </Button>
      <ExternalAuthButtons />
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner
            size={65}
            color={theme.colors.primary}
            backgroundColor="#eee"
          />
        </div>
      )}
    </AuthForm>
  );
};

export default LoginForm;
